import { CommonClientError } from "../../error";
import { DateUtils } from "../../primitive-utils";
import { REGEX_LOCAL_DATE_TIME } from "../../primitive/time/base/time-regex";
import { LocalDateFunc } from "../date/local-date";
import { LocalTimeFunc } from "../time";
import { LOCAL_DATE_TIME_FORMAT, } from "../time-format";
export class LocalDateTimeFunc {
    v;
    constructor(v) {
        this.v = v;
    }
    static get NOW() {
        return LocalDateTimeFunc.fromDateJST(new Date());
    }
    static of({ year, month, day, hour = 0, minute = 0, second = 0, millisecond = 0, }) {
        const date = new Date(year, month - 1, day, hour, minute, second, millisecond);
        date.setFullYear(year); // new Dateに直接渡すと、yearに2桁以下が指定された際に1900年代として自動補完されてしまうのでsetFullYearで別途渡す。
        if (date.getFullYear() !== year ||
            date.getMonth() + 1 !== month ||
            date.getDate() !== day ||
            date.getHours() !== hour ||
            date.getMinutes() !== minute ||
            date.getSeconds() !== second ||
            date.getMilliseconds() !== millisecond) {
            const formattedDateTime = `${year}-${month}-${day}T${hour}:${minute}:${second}.${millisecond}`;
            throw new CommonClientError.InvalidInput(`LocalDateTime invalid error. input[${formattedDateTime}]`, "存在しない日付です。");
        }
        return new LocalDateTimeFunc({
            year,
            month,
            day,
            hour,
            minute,
            second,
            millisecond,
        });
    }
    static parse(text, format = LOCAL_DATE_TIME_FORMAT) {
        const d = DateUtils.fromFormat(text, format);
        if (!DateUtils.isValidDate(d)) {
            throw new CommonClientError.InvalidInput(`LocalDateTime parse error. format[${format}], input[${text}]`, "日付の形式が不正です。");
        }
        return LocalDateTimeFunc.of({
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
            hour: d.getHours(),
            minute: d.getMinutes(),
            second: d.getSeconds(),
            millisecond: d.getMilliseconds(),
        });
    }
    // 正規表現からParseする。
    // ただし、数字部分が yyyy MM dd HH mm ss SSS の順番であることを前提とする。
    static parseFromRegex(text, regex = REGEX_LOCAL_DATE_TIME) {
        if (!regex.test(text)) {
            throw new CommonClientError.InvalidInput(`LocalDateTime regex match error. regex[${regex.source}], input[${text}]`, "日付の形式が不正です。");
        }
        const extractRegex = /\d+/g; // 数字を抽出
        const [year, month, day, hour, minute, second, millisecond] = text.match(extractRegex) ?? [];
        return LocalDateTimeFunc.of({
            year: Number(year ?? 0),
            month: Number(month ?? 0),
            day: Number(day ?? 0),
            hour: Number(hour ?? 0),
            minute: Number(minute ?? 0),
            second: Number(second ?? 0),
            millisecond: Number(millisecond?.padEnd(3, "0").slice(0, 3) ?? 0),
        });
    }
    /** DateをJST解釈のLocalDateTimeに変換して返す */
    static fromDateJST(v) {
        const jstDate = DateUtils.utcToJSTTime(v);
        return this.fromDate(jstDate);
    }
    // USAGE: localDateTimeArray.sort(LocalDateTimeFunc.compere);
    // 日時の昇順 1/1 10:00, 1/1 11:00, 1/2 12:00 で並ぶ
    static compare(a, b) {
        return a.toDate().getTime() - b.toDate().getTime();
    }
    // 日時の降順で並ぶ
    static compareDesc(a, b) {
        return b.toDate().getTime() - a.toDate().getTime();
    }
    toDateJST() {
        return DateUtils.JSTTimeToUtc(this.toDate());
    }
    /** その日の0:00(JST)時点のDateを返す */
    atStartOfDayJST() {
        const d = this.toDate();
        return DateUtils.startOfDayJST(d);
    }
    toLocalDate() {
        return LocalDateFunc.of(this.v);
    }
    toLocalTime() {
        return LocalTimeFunc.of(this.v);
    }
    floor(precision) {
        switch (precision) {
            case "Second":
                return LocalDateTimeFunc.of({ ...this.v, millisecond: 0 });
            case "Minutes":
                return LocalDateTimeFunc.of({ ...this.v, second: 0, millisecond: 0 });
        }
    }
    addDays(days) {
        return this.callAsDate((d) => DateUtils.addDays(d, days));
    }
    addMonths(months) {
        return this.callAsDate((d) => DateUtils.addMonths(d, months));
    }
    addDuration(duration) {
        return this.callAsDate((d) => DateUtils.addSeconds(d, duration.toSeconds()));
    }
    startOfDay() {
        return this.callAsDate((d) => DateUtils.startOfDay(d));
    }
    endOfDay() {
        return this.callAsDate((d) => DateUtils.endOfDay(d));
    }
    setDate(date) {
        return LocalDateTimeFunc.of({ ...this.v, ...date.toValues() });
    }
    equals(other) {
        return this.toDate().getTime() === other.toDate().getTime();
    }
    isAfter(other) {
        return this.toDate().getTime() > other.toDate().getTime();
    }
    isBefore(other) {
        return this.toDate().getTime() < other.toDate().getTime();
    }
    toValues() {
        return this.v;
    }
    toString() {
        const d = this.toDate();
        return DateUtils.format(d, LOCAL_DATE_TIME_FORMAT);
    }
    format(format = LOCAL_DATE_TIME_FORMAT) {
        return DateUtils.format(this.toDate(), format, true);
    }
    // Dateで受け付ける関数を呼びたいときのアダプタ
    callAsDate(func) {
        const d = this.toDate();
        const result = func(d);
        return LocalDateTimeFunc.fromDate(result);
    }
    toDate() {
        const date = new Date(this.v.year, this.v.month - 1, this.v.day, this.v.hour, this.v.minute, this.v.second, this.v.millisecond);
        date.setFullYear(this.v.year); // new Dateに直接渡すと、yearに2桁以下が指定された際に1900年代として自動補完されてしまうのでsetFullYearで別途渡す。
        return date;
    }
    static fromDate(d) {
        return LocalDateTimeFunc.of({
            year: d.getFullYear(),
            month: d.getMonth() + 1,
            day: d.getDate(),
            hour: d.getHours(),
            minute: d.getMinutes(),
            second: d.getSeconds(),
            millisecond: d.getMilliseconds(),
        });
    }
}
//# sourceMappingURL=local-date-time.js.map